//CSS
import "./App.css";

//Router
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import { GoogleOAuthProvider } from "@react-oauth/google";
import { AuthProvider } from "./auth/AuthContext";
import { HelmetProvider } from "react-helmet-async";

// Toast
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Aviso de nova versão (usa o ToastContainer acima; não renderiza nada por si)
import UpdateNotifier from "./components/UpdateNotifier/UpdateNotifier";

// Layouts
import { MarketingLayout, MinimalLayout, AppLayout } from "./layouts";

//Pages
import {
  About,
  Agenda,
  AuthPage,
  AuthSuccess,
  BlocksPage,
  CustomersPage,
  Dashboard,
  FollowUp,
  ForgotPassword,
  Home,
  MessageCenter,
  NotFound,
  Fidelidade,
  PricingPage,
  PlansGridPage,
  PrivacyPolicy,
  ReportsPage,
  ResetPassword,
  ServicesPage,
  SettingsPage,
  TermsOfService,
  TeamPage,
  AcceptInvite,
  LeadsPage,
  SupportPage,
  AccountHealth,
  ProfessionalsPage,
  ProfessionalHoursPage,
  ProfessionalsNewPage,
  UserSettings,
  AgendamentoPage,
  AtendimentoPage,
  AcademiasPage,
  BarbeariasPage,
  ConsultoriosPage,
  MecanicasPage,
  LavaJatosPage,
  WaitlistPage,
  RescheduleBroadcastPage,
  RecursosPage,
  Templates,
  VisaoGeral,
  DepartmentsSettingsPage,
  Onboarding,
} from "./pages";
import { useTenantRole } from "./hooks/useTenantRole";
import ProtectedRoute, { ONBOARDING_PATH } from "./auth/ProtectedRoute";
import SchedulingRoute from "./components/SchedulingUpsell/SchedulingRoute";

/**
 * RoleRoute — proteção de rota por papel no tenant. Renderiza os filhos apenas
 * quando o papel do usuário (OWNER | ADMIN | MEMBER) está na lista `roles`.
 * Deve ficar DENTRO de <ProtectedRoute> (o token já existe). Enquanto o papel
 * carrega, não renderiza nada; se não permitido, redireciona para /visao-geral.
 */
function RoleRoute({ roles, children }) {
  const { role, loading } = useTenantRole();
  if (loading) return null;
  if (!role || !roles.includes(role)) {
    return <Navigate to="/visao-geral" replace />;
  }
  return children;
}

function AppContent() {
  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <UpdateNotifier />
      <Routes>
        {/* ── Marketing (header transparente → branco + footer) ── */}
        <Route
          path="/"
          element={
            <MarketingLayout>
              <Home />
            </MarketingLayout>
          }
        />
        <Route
          path="/about"
          element={
            <MarketingLayout>
              <About />
            </MarketingLayout>
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <MarketingLayout>
              <PrivacyPolicy />
            </MarketingLayout>
          }
        />
        <Route
          path="/terms-of-use"
          element={
            <MarketingLayout>
              <TermsOfService />
            </MarketingLayout>
          }
        />
        {/* Páginas de PRODUTO. Cada uma manda o visitante ao cadastro já com o
            `?produto=`, e é isso que decide o plano de trial no backend
            (FREE × FREE_ATENDIMENTO). As de segmento, abaixo, vendem
            agendamento e por isso apontam para o produto Agendamento. */}
        <Route
          path="/agendamento"
          element={
            <MarketingLayout>
              <AgendamentoPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/atendimento"
          element={
            <MarketingLayout>
              <AtendimentoPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/barbearias"
          element={
            <MarketingLayout>
              <BarbeariasPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/consultorios"
          element={
            <MarketingLayout>
              <ConsultoriosPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/mecanicas"
          element={
            <MarketingLayout>
              <MecanicasPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/lava-jatos"
          element={
            <MarketingLayout>
              <LavaJatosPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/academias"
          element={
            <MarketingLayout>
              <AcademiasPage />
            </MarketingLayout>
          }
        />
        <Route
          path="/recursos"
          element={
            <MarketingLayout>
              <RecursosPage />
            </MarketingLayout>
          }
        />

        {/* ── Minimal (header simples sem CTAs, sem sticky) ── */}
        <Route
          path="/login"
          element={
            <MinimalLayout>
              <AuthPage />
            </MinimalLayout>
          }
        />
        <Route
          path="/registration"
          element={<Navigate to="/login?tab=signup" replace />}
        />
        <Route
          path="/forgot-password"
          element={
            <MinimalLayout>
              <ForgotPassword />
            </MinimalLayout>
          }
        />
        <Route
          path="/reset-password"
          element={
            <MinimalLayout>
              <ResetPassword />
            </MinimalLayout>
          }
        />

        {/* ── Sem header (rotas especiais) ── */}
        <Route path="/auth-success" element={<AuthSuccess />} />
        <Route path="/invite/accept/:token" element={<AcceptInvite />} />
        {/* Onboarding obrigatório — protegido (exige token), mas SEM AppLayout:
            o menu lateral é derivado do segmento do tenant, e é justamente o
            tenant que ainda não existe. O `ProtectedRoute` se abstém de
            redirecionar quando já se está aqui. */}
        <Route
          path={ONBOARDING_PATH}
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* ── App (área autenticada — AppHeader fixo + Sidebar nas páginas) ── */}
        <Route
          path="/visao-geral"
          element={
            <ProtectedRoute>
              <AppLayout>
                <VisaoGeral />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agenda"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <Agenda />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user-settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UserSettings />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Path antigo (bare) redireciona para a gestão. */}
        <Route
          path="/subscription"
          element={<Navigate to="/subscription/billing" replace />}
        />
        {/* Gestão da assinatura ("Plano e cobrança") — destino primário. */}
        <Route
          path="/subscription/billing"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PricingPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Grade "Ver todos os planos" (modo gestão). */}
        <Route
          path="/subscription/plans"
          element={
            <ProtectedRoute>
              <AppLayout>
                <PlansGridPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/services"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ServicesPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/settings"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SettingsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/messages-center"
          element={
            <ProtectedRoute>
              <AppLayout>
                <MessageCenter />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/reports"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ReportsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/customers"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CustomersPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/team"
          element={
            <ProtectedRoute>
              <AppLayout>
                <TeamPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/settings/departments"
          element={
            <ProtectedRoute>
              <AppLayout>
                <RoleRoute roles={["OWNER", "ADMIN"]}>
                  <DepartmentsSettingsPage />
                </RoleRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/leads"
          element={
            <ProtectedRoute>
              <AppLayout>
                <LeadsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/support"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SupportPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/health"
          element={
            <ProtectedRoute>
              <AppLayout>
                <AccountHealth />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/professionals"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <ProfessionalsPage />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/professionals/new"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <ProfessionalsNewPage />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/professionals/hours"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <ProfessionalHoursPage />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/blocks"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <BlocksPage />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/follow-up"
          element={
            <ProtectedRoute>
              <AppLayout>
                <FollowUp />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/waitlist"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <WaitlistPage />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/reschedule-broadcast"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <RescheduleBroadcastPage />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fidelidade"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SchedulingRoute>
                  <Fidelidade />
                </SchedulingRoute>
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/templates"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Templates />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* ── Catch-all (404) — SEMPRE a última rota ──
            Público de propósito: fica FORA de <ProtectedRoute>, senão uma URL
            errada mandaria o visitante deslogado para /login em vez de
            explicar o erro. Usa MarketingLayout como as demais páginas
            públicas, para o usuário ter header/footer de saída. */}
        <Route
          path="*"
          element={
            <MarketingLayout>
              <NotFound />
            </MarketingLayout>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId="10016738900-p58t59j9dsdbi8jbefpoekckdk1f4bp6.apps.googleusercontent.com">
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
